import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Refresh,
  PlayArrow,
  CheckCircle,
  Schedule,
  Assignment,
  Person,
} from '@mui/icons-material';

const API_BASE =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';

interface ScheduledOperation {
  id: string;
  workOrderId: string;
  workOrderNumber: string;
  operationDescription: string;
  workCenterId: string;
  workCenterCode: string;
  itemCode: string;
  itemDescription: string;
  plannedQuantity: number;
  unit: string;
  priority: number;
  scheduledStart: string;
  scheduledEnd: string;
  setupTimeMinutes: number;
  runTimeMinutes: number;
  status: string;
  assignedOperatorId: string | null;
  assignedOperatorName: string | null;
  notes: string | null;
}

interface WorkCenter {
  id: string;
  code: string;
  name: string;
}

const priorityColor = (p: number) => {
  if (p <= 2) return 'error';
  if (p <= 4) return 'warning';
  return 'default';
};

const priorityLabel = (p: number) => {
  if (p === 1) return 'Critical';
  if (p <= 3) return 'High';
  if (p <= 5) return 'Medium';
  return 'Low';
};

const statusColor = (s: string): 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' => {
  switch (s) {
    case 'scheduled': return 'info';
    case 'queued': return 'warning';
    case 'in_progress': return 'primary';
    case 'completed': return 'success';
    case 'cancelled': return 'error';
    case 'on_hold': return 'default';
    default: return 'default';
  }
};

export default function Dispatch() {
  const [workCenters, setWorkCenters] = useState<WorkCenter[]>([]);
  const [selectedWorkCenter, setSelectedWorkCenter] = useState<string>('');
  const [queue, setQueue] = useState<ScheduledOperation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch available work centers from master-data
  useEffect(() => {
    const fetchWorkCenters = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE}/master-data/work-centers`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        const body = await res.json();
        const wcs = Array.isArray(body?.data)
          ? body.data.map((wc: any) => ({ id: wc.id, code: wc.code || wc.workCenterCode, name: wc.name || wc.description || wc.code }))
          : Array.isArray(body)
          ? body.map((wc: any) => ({ id: wc.id, code: wc.code || wc.workCenterCode, name: wc.name || wc.description || wc.code }))
          : [];
        setWorkCenters(wcs);
        if (wcs.length > 0 && !selectedWorkCenter) {
          setSelectedWorkCenter(wcs[0].id);
        }
      } catch {
        // Work centers might not be available yet
      }
    };
    fetchWorkCenters();
  }, []);

  const fetchQueue = useCallback(async () => {
    if (!selectedWorkCenter) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_BASE}/scheduling/dispatch-list?workCenterId=${selectedWorkCenter}`,
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );
      if (!res.ok) {
        throw new Error(`Failed to fetch dispatch queue (HTTP ${res.status})`);
      }
      const body = await res.json();
      // The dispatch endpoint wraps the list: data = { workCenterId, date, operations: [...] }
      const payload = body?.data ?? body;
      const ops: ScheduledOperation[] = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.operations)
        ? payload.operations
        : [];
      setQueue(ops);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch dispatch queue');
      setQueue([]);
    } finally {
      setLoading(false);
    }
  }, [selectedWorkCenter]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchQueue, 30000);
    return () => clearInterval(interval);
  }, [fetchQueue]);

  const handleStatusUpdate = async (opId: string, newStatus: string) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE}/scheduling/operations/${opId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ status: newStatus }),
      });
      fetchQueue();
    } catch (err: any) {
      setError(err.message || 'Failed to update status');
    }
  };

  const selectedWcName =
    workCenters.find((wc) => wc.id === selectedWorkCenter)?.name || 'Select a work center';

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Dispatch Queue
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Operator-facing work queue ordered by priority. Select a work center to view scheduled operations.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchQueue}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Work center selector */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Work Center</InputLabel>
            <Select
              value={selectedWorkCenter}
              label="Work Center"
              onChange={(e) => setSelectedWorkCenter(e.target.value)}
            >
              {workCenters.map((wc) => (
                <MenuItem key={wc.id} value={wc.id}>
                  {wc.code} - {wc.name}
                </MenuItem>
              ))}
              {workCenters.length === 0 && (
                <MenuItem disabled value="">
                  No work centers found
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                  <Typography variant="caption" color="text.secondary">
                    Queued Operations
                  </Typography>
                  <Typography variant="h5">{queue.length}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={4}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                  <Typography variant="caption" color="text.secondary">
                    High Priority
                  </Typography>
                  <Typography variant="h5" color="error.main">
                    {queue.filter((op) => op.priority <= 3).length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={4}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                  <Typography variant="caption" color="text.secondary">
                    Work Center
                  </Typography>
                  <Typography variant="h6" noWrap>
                    {selectedWcName}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && queue.length === 0 ? (
        <Box>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </Box>
      ) : queue.length === 0 && !loading ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No operations in the dispatch queue for this work center. All caught up!
        </Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.50' }}>
                <TableCell>Priority</TableCell>
                <TableCell>Work Order</TableCell>
                <TableCell>Operation</TableCell>
                <TableCell>Item</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell>Scheduled Start</TableCell>
                <TableCell>Setup + Run</TableCell>
                <TableCell>Operator</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {queue.map((op) => (
                <TableRow key={op.id} hover>
                  <TableCell>
                    <Chip
                      label={priorityLabel(op.priority)}
                      color={priorityColor(op.priority) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {op.workOrderNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>{op.operationDescription}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{op.itemCode}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {op.itemDescription}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {op.plannedQuantity} {op.unit}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(op.scheduledStart).toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {op.setupTimeMinutes}m + {op.runTimeMinutes}m
                  </TableCell>
                  <TableCell>
                    {op.assignedOperatorName ? (
                      <Chip
                        icon={<Person />}
                        label={op.assignedOperatorName}
                        size="small"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Unassigned
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={op.status} color={statusColor(op.status)} size="small" />
                  </TableCell>
                  <TableCell align="center">
                    {op.status === 'scheduled' && (
                      <Tooltip title="Start operation">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleStatusUpdate(op.id, 'in_progress')}
                        >
                          <PlayArrow />
                        </IconButton>
                      </Tooltip>
                    )}
                    {op.status === 'in_progress' && (
                      <Tooltip title="Complete operation">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleStatusUpdate(op.id, 'completed')}
                        >
                          <CheckCircle />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
